/**
 * Who is working when, as the board reads it (M1-39).
 *
 * <b>The state comes from the server, already resolved for each date.</b> The
 * template's effective windows, the time-off ranges and the weekday lookup are
 * all civil-date arithmetic, and doing them in one place server-side
 * (`TechnicianAvailabilityCalendar`) is what stops the board's greying and the
 * tooltip's own summary line from disagreeing. This module only indexes that
 * answer and says what the UI should do with it.
 *
 * The timezone question is settled one step earlier and is worth stating here
 * because this is where it would be easy to reintroduce: the board already
 * decides which calendar date it is showing on the ACCOUNT's clock
 * (`todayInZone`, and the `date` URL parameter), and that is the date handed to
 * the API. Nothing here turns a date into an instant, so there is no second
 * clock to get wrong.
 */
import type { components } from '@/api/generated/schema';
import { toIsoDate, type CivilDate } from './timezone';

export type TechnicianDaySummary = components['schemas']['TechnicianDaySummaryResponse'];
export type TechnicianDayState = components['schemas']['TechnicianDayState'];

/**
 * What the server says about one technician on one date.
 *
 * The four states are `TechnicianDayAvailabilityState`'s names. They cross the
 * wire as strings for the reason every other enum here does — an ordinal is one
 * off-by-one from meaning something else entirely.
 */
export type AvailabilityState = 'Unknown' | 'Available' | 'OutsideTemplate' | 'TimeOff';

/** Availability by user id, then by ISO date. */
export type AvailabilityMap = Map<string, Map<string, TechnicianDayState>>;

/** Everything else the tooltip shows, by user id. */
export type TechnicianDetailMap = Map<string, TechnicianDaySummary>;

export interface TechnicianBoardData {
  availability: AvailabilityMap;
  details: TechnicianDetailMap;
}

/** Indexes one windowed response for the two lookups the board makes. */
export function indexTechnicianDays(
  summaries: readonly TechnicianDaySummary[],
): TechnicianBoardData {
  const availability: AvailabilityMap = new Map();
  const details: TechnicianDetailMap = new Map();

  for (const summary of summaries) {
    details.set(summary.userId, summary);
    const byDate = new Map<string, TechnicianDayState>();
    for (const day of summary.days) {
      byDate.set(day.date, day);
    }
    availability.set(summary.userId, byDate);
  }

  return { availability, details };
}

/** One technician's state on one date, or undefined if it was not fetched. */
export function availabilityOn(
  availability: AvailabilityMap,
  userId: string,
  date: CivilDate,
): TechnicianDayState | undefined {
  return availability.get(userId)?.get(toIsoDate(date));
}

/**
 * Whether the board should grey this technician out on this day.
 *
 * ⚠️ <b>`Unknown` is NOT unavailable, and this is the single most consequential
 * line in the story.</b> Every technician is in that state until somebody fills
 * a template in, so greying it would grey the entire board on the day this
 * ships — the failure mode api/CONVENTIONS.md's M1-38 section describes for a
 * guard that fires on 100% of the real rows rather than the edge case it was
 * imagined for. The real dev account has three technicians and no availability
 * rows at all.
 *
 * A MISSING entry (the request has not landed, or failed) is treated the same
 * way, for the same reason and one more: a board that greys people out because
 * a request failed is telling the dispatcher something false about their crew.
 */
export function isUnavailable(state: TechnicianDayState | undefined): boolean {
  return state?.availabilityState === 'OutsideTemplate' || state?.availabilityState === 'TimeOff';
}
